import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';
import { IconAlert, IconInbox } from './icons';
import { useKit } from './strings';

/* ---------- Card ---------- */

export type KitCardProps = HTMLAttributes<HTMLElement> & {
  as?: 'section' | 'article' | 'div' | 'li';
  elevated?: boolean;
  selected?: boolean;
  padding?: 'default' | 'none';
};

/** Soft rounded surface with a hairline border. Use CardHeader / CardBody / CardFooter inside. */
export function Card({ as: Tag = 'section', elevated, selected, padding = 'default', className, ...rest }: KitCardProps) {
  return <Tag {...rest} data-elevated={elevated || undefined} data-selected={selected || undefined} data-padding={padding} className={cx('qk-card', className)} />;
}

/** A whole-card action (a real button, so it is keyboard and screen-reader reachable). */
export function CardButton({ className, selected, type = 'button', ...rest }: HTMLAttributes<HTMLButtonElement> & { selected?: boolean; type?: 'button' | 'submit' }) {
  return <button {...rest} type={type} data-interactive="" data-selected={selected || undefined} aria-pressed={selected} className={cx('qk-card', className)} />;
}

export function CardHeader({ title, description, actions, level = 2, id }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; level?: 2 | 3 | 4; id?: string }) {
  const H = `h${level}` as 'h2' | 'h3' | 'h4';
  return (
    <header className="qk-card__header">
      <div className="qk-card__heading">
        <H className="qk-card__title" id={id}>{title}</H>
        {description ? <p className="qk-card__desc">{description}</p> : null}
      </div>
      {actions ? <div className="qk-card__actions">{actions}</div> : null}
    </header>
  );
}

export const CardBody = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => <div {...rest} className={cx('qk-card__body', className)} />;
export const CardFooter = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => <div {...rest} className={cx('qk-card__footer', className)} />;

/* ---------- Badge ---------- */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/** Status pill. Tone is never the only signal: the label always says the state in words. */
export function Badge({ tone = 'neutral', variant = 'soft', dot, children, className }: { tone?: BadgeTone; variant?: 'soft' | 'outline'; dot?: boolean; children: ReactNode; className?: string }) {
  return (
    <span className={cx('qk-badge', className)} data-tone={tone} data-variant={variant}>
      {dot ? <span className="qk-badge__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/* ---------- Skeleton ---------- */

/** Placeholder shimmer. Hidden from assistive tech; wrap a loading region in <LoadingRegion> to announce it. */
export function Skeleton({ width, height, shape = 'line', className }: { width?: string; height?: string; shape?: 'line' | 'circle' | 'block'; className?: string }) {
  return <span aria-hidden="true" data-shape={shape} className={cx('qk-skeleton', className)} style={{ inlineSize: width, blockSize: height }} />;
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <span className="qk-skeleton-text" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => <Skeleton key={i} width={i === lines - 1 && lines > 1 ? '60%' : '100%'} />)}
    </span>
  );
}

/** Marks a region busy and announces "Loading" once, while the skeleton children stay silent. */
export function LoadingRegion({ label, children }: { label?: string; children: ReactNode }) {
  const { strings } = useKit();
  return (
    <div aria-busy="true">
      <span className="qk-sr-only" role="status">{label ?? strings.loading}</span>
      {children}
    </div>
  );
}

/* ---------- EmptyState ---------- */

/**
 * Empty, zero-data and error states: icon tile, one plain sentence, the next action. tone="error" announces itself
 * (role="alert") and uses the warning glyph; always offer a retry or a way out.
 */
export function EmptyState({ title, description, icon, actions, tone = 'default', size = 'md', headingLevel = 2 }: {
  title: ReactNode; description?: ReactNode; icon?: ReactNode; actions?: ReactNode; tone?: 'default' | 'error'; size?: 'sm' | 'md'; headingLevel?: 2 | 3 | 4;
}) {
  const H = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    <div className="qk-empty" data-tone={tone} data-size={size} role={tone === 'error' ? 'alert' : undefined}>
      <span className="qk-empty__icon" aria-hidden="true">{icon ?? (tone === 'error' ? <IconAlert /> : <IconInbox />)}</span>
      <H className="qk-empty__title">{title}</H>
      {description ? <p className="qk-empty__desc">{description}</p> : null}
      {actions ? <div className="qk-empty__actions">{actions}</div> : null}
    </div>
  );
}
